/**
 * FeedbackButtons - Thumbs up/down feedback collection for training pipeline
 *
 * Phase 1.1: Feedback UI Implementation
 * - Collects binary feedback (positive/negative)
 * - Auto-creates training examples from positive feedback (via Edge Function)
 * - Tracks PostHog analytics events
 * - i18n compliant (all user-facing text uses t())
 *
 * Implementation:
 * - Server-side submission via feedback-submit Edge Function
 * - Eliminates client-side JWT/RLS issues
 * - Guaranteed auth.uid() consistency
 *
 * Related:
 * - workers/src/functions/feedback-submit.ts — CF Worker handler
 * - workers/src/functions/get-feedback.ts — existing-feedback lookup
 * - packages/shared/src/schemas/feedback.ts — DTO contracts
 * - Phase 0.5b LLM Training Infrastructure
 */

import { View, Pressable, Text, ActivityIndicator } from 'react-native';
import { useState, useEffect } from 'react';
import { useTranslation } from '@dramadetector/shared';
import { useAuth } from '../contexts/AuthContext';
import { captureEvent } from '../lib/analytics';
import { debugLog } from '../lib/debug-logger';
import { Ionicons } from '@expo/vector-icons';
import { useFeedbackSubmit, useGetFeedback } from '../hooks/useFeedbackSubmit';

interface FeedbackButtonsProps {
  /** Analysis ID from analysis_history table */
  analysisId: string;

  /** Analysis score (for analytics) */
  analysisScore: number;

  /** Test ID for E2E testing */
  testID?: string;
}

/**
 * Feedback Buttons Component
 *
 * Displays thumbs up/down buttons for collecting user feedback
 * on analysis quality. Feedback is stored in analysis_feedback table
 * and automatically creates training examples for positive feedback.
 */
export function FeedbackButtons({
  analysisId,
  analysisScore,
  testID = 'feedback-buttons',
}: FeedbackButtonsProps) {
  const { t } = useTranslation('analysis');
  const { user } = useAuth();
  const [feedbackGiven, setFeedbackGiven] = useState<'positive' | 'negative' | null>(null);

  // Read existing feedback via validated hook. Disabled for anonymous users
  // (they can't have stored feedback) to avoid unnecessary 404 traffic.
  const existingFeedback = useGetFeedback(user?.id ? analysisId : undefined);
  const checkingExisting = user?.id ? existingFeedback.isLoading : false;

  // Submit via validated mutation hook. Centralizes the DTO parse + 409
  // dedup surface so FeedbackButtons stays focused on UX.
  const submitFeedback = useFeedbackSubmit();
  const submitting = submitFeedback.isPending;

  /**
   * Mirror the fetched feedback into local state so the optimistic UX
   * (setting `feedbackGiven` after mutate) still works. Runs when the query
   * transitions to success with a non-null feedback row.
   */
  useEffect(() => {
    const fetched = existingFeedback.data?.feedback;
    if (fetched && (fetched.type === 'positive' || fetched.type === 'negative')) {
      setFeedbackGiven(fetched.type);
      captureEvent('Feedback_Existing_Loaded', {
        analysis_id: analysisId,
        feedback_type: fetched.type,
      });
    }
    // Surface query errors other than "not owned" (404) for operator
    // visibility but never block the UI — feedback is best-effort.
    if (existingFeedback.error && __DEV__) {
      console.warn('[FeedbackButtons] Error checking existing feedback:', existingFeedback.error);
    }
  }, [existingFeedback.data, existingFeedback.error, analysisId]);

  const handleFeedback = async (feedbackType: 'positive' | 'negative') => {
    if (feedbackGiven || submitting) return; // Prevent duplicate submissions

    // Track analytics event (BEFORE database write for immediate feedback)
    captureEvent('Analysis_Feedback_Submitted', {
      feedback_type: feedbackType,
      analysis_id: analysisId,
      analysis_score: analysisScore,
      feedback_source: 'analysis_result',
    });

    // Submit feedback via validated mutation (DTO-enforced wire contract)
    debugLog('event', 'Calling feedback-submit via useFeedbackSubmit', { analysisId });

    try {
      const result = await submitFeedback.mutateAsync({
        analysis_id: analysisId,
        feedback_type: feedbackType,
        feedback_source: 'analysis_result',
      });

      setFeedbackGiven(feedbackType);

      captureEvent('Analysis_Feedback_Success', {
        feedback_type: feedbackType,
        feedback_id: result.feedback_id,
        analysis_score: analysisScore,
      });
    } catch (error) {
      console.error('[FeedbackButtons] Error submitting feedback:', error);

      captureEvent('Analysis_Feedback_Error', {
        feedback_type: feedbackType,
        error: error instanceof Error ? error.message : 'Unknown error',
        analysis_score: analysisScore,
      });
    }
  };

  // Loading state while checking for existing feedback
  if (checkingExisting) {
    return (
      <View className="mt-6 mb-4" testID={testID}>
        <View className="flex-row justify-center items-center gap-2">
          <ActivityIndicator size="small" color="#6b7280" />
          <Text className="text-gray-500 text-sm">
            {t('analysis.feedback.loading', { defaultValue: 'Loading...' })}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View className="mt-6 mb-4" testID={testID}>
      {/* Section Header */}
      <Text className="text-center text-gray-700 dark:text-gray-300 font-semibold mb-3">
        {t('feedback.title')}
      </Text>

      {/* Feedback Buttons */}
      <View className="flex-row justify-center gap-4">
        {/* Thumbs Up */}
        <Pressable
          onPress={() => handleFeedback('positive')}
          disabled={submitting || feedbackGiven !== null}
          className={`
            px-6 py-3 rounded-xl flex-row items-center gap-2
            ${feedbackGiven === 'positive' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border-2 border-gray-300'}
            ${submitting || feedbackGiven !== null ? 'opacity-50' : 'active:opacity-70'}
          `}
          testID={`${testID}-thumbs-up`}
        >
          <Ionicons
            name={feedbackGiven === 'positive' ? 'thumbs-up' : 'thumbs-up-outline'}
            size={24}
            color={feedbackGiven === 'positive' ? '#10b981' : '#6b7280'}
          />
          <Text
            className={`font-semibold ${feedbackGiven === 'positive' ? 'text-green-700' : 'text-gray-700'}`}
          >
            {t('feedback.thumbs_up')}
          </Text>
        </Pressable>

        {/* Thumbs Down */}
        <Pressable
          onPress={() => handleFeedback('negative')}
          disabled={submitting || feedbackGiven !== null}
          className={`
            px-6 py-3 rounded-xl flex-row items-center gap-2
            ${feedbackGiven === 'negative' ? 'bg-red-100 border-2 border-red-500' : 'bg-white border-2 border-gray-300'}
            ${submitting || feedbackGiven !== null ? 'opacity-50' : 'active:opacity-70'}
          `}
          testID={`${testID}-thumbs-down`}
        >
          <Ionicons
            name={feedbackGiven === 'negative' ? 'thumbs-down' : 'thumbs-down-outline'}
            size={24}
            color={feedbackGiven === 'negative' ? '#ef4444' : '#6b7280'}
          />
          <Text
            className={`font-semibold ${feedbackGiven === 'negative' ? 'text-red-700' : 'text-gray-700'}`}
          >
            {t('feedback.thumbs_down')}
          </Text>
        </Pressable>
      </View>

      {/* Thank You Message (shown after feedback submission or when existing feedback loaded) */}
      {feedbackGiven && (
        <Text className="text-center text-gray-600 dark:text-gray-400 text-sm mt-3">
          {t('analysis.feedback.already_submitted', {
            defaultValue: 'Thanks for your feedback!'
          })}
        </Text>
      )}
    </View>
  );
}
